import React from "react";
import clsx from "clsx";
import {
  IonIcon,
  IonLabel,
  IonItem,
  IonReorderGroup,
  IonReorder,
  IonButton,
} from "@ionic/react";
import {
  removeCircleOutline as removeIcon,
  eyeOutline as visibleIcon,
  eyeOffOutline as hiddenIcon,
} from "ionicons/icons";

import {
  ITeacherGearbox,
  ITeacher,
  CreateGearboxParams,
  UpdateGearboxParams,
  TeacherGearboxDirectory,
  GearboxTemplate,
} from "../../../../state";
import { BoxState } from "../../../../pages/teacher/ManageBoxesPage/types";
import { buildSharedTeachersLabel } from "../../../../libs/Util";
import BoxTitle from "../../../../atoms/BoxTitle";
import ToolTip from "../../../../atoms/ToolTip";
import ModalConfirm from "../../../../molecules/ModalConfirm";

import styles from "./ManageBoxesMenu.module.scss";

type ManageBoxesMenuProps = {
  calling: boolean;
  teacher: ITeacher;
  teacherGearboxDirectories: TeacherGearboxDirectory[];
  gearboxes: ITeacherGearbox[];
  templates: GearboxTemplate[];
  editable: boolean;
  state: BoxState;
  setState: React.Dispatch<React.SetStateAction<BoxState>>;
  gearboxHidden: (id: number) => void;
  createGearbox: (args: CreateGearboxParams) => void;
  updateGearbox: (args: UpdateGearboxParams) => void;
  deleteGearbox: (id: number) => void;
  createTemplate: () => void;
  update: (args: { [key: string]: any }) => void;
};

const ManageBoxesMenu: React.FC<ManageBoxesMenuProps> = ({
  calling,
  teacher,
  gearboxes,
  teacherGearboxDirectories,
  editable,
  state,
  setState,
  gearboxHidden,
  deleteGearbox,
  update,
}) => {
  function doReorder(event: CustomEvent) {
    const boxes = event.detail.complete(gearboxes);
    update({
      boxes,
    });
  }

  return (
    <div className={styles.wrapper}>
      <ModalConfirm
        loading={calling}
        isOpen={state.showDeleteBoxConfirm}
        onClose={() => setState(s => ({ ...s, showDeleteBoxConfirm: false }))}
        whiteMode
        message={
          <>
            「{state.gearboxTitle}」を削除しますか？
            <br />
            {state.subject_id && (
              <>
                削除すると対象クラスの教科登録も解除され、一緒に使う先生からも表示されなくなります。
                <br />
              </>
            )}
            BOXに提出されたストックは削除されません。
            <br />
            また、この操作は取り消しができません。
          </>
        }
        buttons={[
          {
            title: "キャンセル",
            type: "None",
            action: () =>
              setState(s => ({ ...s, showDeleteBoxConfirm: false })),
          },
          {
            title: "削除する",
            type: "Danger",
            action: () => {
              deleteGearbox(state.gearboxID);
              setState(s => ({ ...s, showDeleteBoxConfirm: false }));
            },
          },
        ]}
      />
      <IonReorderGroup
        disabled={!editable || calling}
        onIonItemReorder={doReorder}
      >
        {gearboxes &&
          gearboxes.map((b, i) => {
            const classLabel = b.klasses?.map(cls => cls.full_name).join(", ");
            const hasCoTeachers =
              !b.is_shared &&
              (b.assigned_teachers ?? []).some(t => t.id !== teacher.id);
            return (
              <IonItem
                key={b.id}
                className={clsx(
                  styles.boxItem,
                  b.is_hidden && styles.hiddenBoxItem,
                )}
                lines={i + 1 === gearboxes.length ? "none" : "inset"}
              >
                {editable &&
                  (b.subject_id && (b.realizations_count ?? 0) > 0 ? (
                    <div
                      slot="start"
                      className={styles.subjectLinkedRemoveButtonWrapper}
                    >
                      <ToolTip
                        content="ストックが提出された教科BOXは削除できません。提出を停止をご利用ください"
                        placement="right"
                      >
                        <IonButton
                          className={clsx(
                            styles.removeButton,
                            styles.subjectLinkedRemoveButton,
                          )}
                          fill="clear"
                          size="large"
                          disabled
                        >
                          <IonIcon icon={removeIcon} />
                        </IonButton>
                      </ToolTip>
                    </div>
                  ) : (
                    <IonButton
                      className={styles.removeButton}
                      disabled={calling}
                      slot="start"
                      fill="clear"
                      size="large"
                      onClick={() => {
                        setState(s => ({
                          ...s,
                          gearboxID: b.id,
                          gearboxTitle: b.title,
                          gearboxTemplateId: b.gearbox_template_id,
                          teacherGearboxDirectoryIDs:
                            b.teacher_gearbox_directory_ids,
                          nameStatus: b.name_status ?? "unpublish",
                          is_archived: b.is_archived,
                          subject_id: b.subject_id ?? null,
                          showDeleteBoxConfirm: true,
                        }));
                      }}
                    >
                      <IonIcon icon={removeIcon} />
                    </IonButton>
                  ))}
                <IonLabel
                  className={clsx(
                    styles.boxItemLabel,
                    editable && styles.pointer,
                  )}
                  onClick={() => {
                    if (editable) {
                      setState(s => ({
                        ...s,
                        gearboxID: b.id,
                        gearboxTitle: b.title,
                        gearboxTemplateId: b.gearbox_template_id,
                        classIDs: b.klasses?.map(cls => cls.id) ?? [],
                        teacherGearboxDirectoryIDs:
                          b.teacher_gearbox_directory_ids,
                        nameStatus: b.name_status ?? "unpublish",
                        is_submission_private: b.is_submission_private ?? false,
                        is_archived: b.is_archived,
                        is_shared: b.is_shared,
                        subject_id: b.subject_id ?? null,
                        coach_types: b.coach_types,
                        showBoxForm: true,
                      }));
                    }
                  }}
                >
                  <div className={styles.boxTitle}>
                    <BoxTitle
                      isArchived={b.is_archived}
                      isSubmissionPrivate={b.is_submission_private}
                      isSubjectBox={!!b.subject_id}
                      nameStatus={b.name_status}
                      title={b.title}
                      gearboxDirectoryIds={b.teacher_gearbox_directory_ids}
                      year={b.year}
                      teacherGearboxDirectories={teacherGearboxDirectories}
                      sharedTeachersLabel={
                        hasCoTeachers
                          ? buildSharedTeachersLabel(
                              b.assigned_teachers,
                              teacher.id,
                            )
                          : undefined
                      }
                    />
                  </div>
                  {classLabel && (
                    <div className={styles.classLabel}>{classLabel}</div>
                  )}
                </IonLabel>
                {editable && (
                  <IonButton fill="clear" onClick={() => gearboxHidden(b.id)}>
                    <IonIcon
                      icon={b.is_hidden ? hiddenIcon : visibleIcon}
                      className={styles.eyeIcon}
                    />
                  </IonButton>
                )}
                <IonReorder slot="end" className={styles.reorderIcon} />
              </IonItem>
            );
          })}
      </IonReorderGroup>
    </div>
  );
};

export default ManageBoxesMenu;
